import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { DEFAULT_LDB_STATE, DEFAULT_SETTINGS, createId } from '../data/ldbDemoData';
import * as ldbApi from '../lib/ldb/api';

const LdbContext = createContext(null);

const COLLECTIONS = ['funders', 'grants', 'partners', 'subscribers', 'volunteers', 'followups'];

function emptyState() {
  const s = { settings: { ...DEFAULT_SETTINGS }, activityLog: [] };
  COLLECTIONS.forEach((c) => { s[c] = []; });
  return s;
}

export function LdbProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(emptyState);
  const [lang, setLang] = useState('en');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // When Supabase is not configured (e.g. local dev), run in-memory with demo data.
  const useLocal = !supabase;
  const actorEmail = user?.email || null;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        if (useLocal) {
          setState({ ...DEFAULT_LDB_STATE, activityLog: [] });
        } else {
          const next = await ldbApi.fetchState();
          if (mounted) setState(next);
        }
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load dashboard data');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [useLocal]);

  const pushLocalActivity = useCallback((action, detail) => {
    const entry = { id: createId('log'), when: new Date().toISOString(), action, detail };
    setState((s) => ({ ...s, activityLog: [entry, ...s.activityLog].slice(0, 200) }));
  }, []);

  const logActivity = useCallback(async (action, detail) => {
    if (useLocal) { pushLocalActivity(action, detail); return; }
    try {
      const entry = await ldbApi.logActivity(action, detail, actorEmail);
      setState((s) => ({ ...s, activityLog: [entry, ...s.activityLog].slice(0, 200) }));
    } catch {
      /* activity logging is best-effort */
    }
  }, [useLocal, actorEmail, pushLocalActivity]);

  const upsertItem = useCallback(async (collection, item, label) => {
    const isEdit = !!item.id;
    const name = item.organization || item.name || item.program || '';
    try {
      if (useLocal) {
        const saved = isEdit ? item : { ...item, id: createId(collection.slice(0, 2)) };
        setState((s) => {
          const list = s[collection] || [];
          const next = isEdit ? list.map((x) => (x.id === saved.id ? saved : x)) : [...list, saved];
          return { ...s, [collection]: next };
        });
        pushLocalActivity(isEdit ? `${label} edited` : `${label} added`, name);
        return;
      }
      const saved = isEdit
        ? await ldbApi.updateRecord(collection, item.id, item)
        : await ldbApi.createRecord(collection, item);
      setState((s) => {
        const list = s[collection] || [];
        const next = isEdit ? list.map((x) => (x.id === saved.id ? saved : x)) : [...list, saved];
        return { ...s, [collection]: next };
      });
      logActivity(isEdit ? `${label} edited` : `${label} added`, name);
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  }, [useLocal, logActivity, pushLocalActivity]);

  const deleteItem = useCallback(async (collection, id, label, name) => {
    try {
      if (!useLocal) await ldbApi.deleteRecord(collection, id);
      setState((s) => ({ ...s, [collection]: (s[collection] || []).filter((x) => x.id !== id) }));
      if (useLocal) pushLocalActivity(`${label} deleted`, name); else logActivity(`${label} deleted`, name);
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  }, [useLocal, logActivity, pushLocalActivity]);

  const updateSettings = useCallback(async (settings) => {
    try {
      if (useLocal) {
        setState((s) => ({ ...s, settings: { ...s.settings, ...settings } }));
        pushLocalActivity('Settings updated', 'Organization targets saved');
        return;
      }
      const saved = await ldbApi.saveSettings(settings);
      setState((s) => ({ ...s, settings: saved }));
      logActivity('Settings updated', 'Organization targets saved');
    } catch (e) {
      setError(e.message || 'Settings save failed');
    }
  }, [useLocal, logActivity, pushLocalActivity]);

  const clearActivityLog = useCallback(async () => {
    try {
      if (!useLocal) await ldbApi.clearActivity();
      setState((s) => ({ ...s, activityLog: [] }));
    } catch (e) {
      setError(e.message || 'Could not clear log');
    }
  }, [useLocal]);

  const importData = useCallback((json) => {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    setState({ ...emptyState(), ...parsed });
    pushLocalActivity('Data imported', 'Local view replaced (not persisted)');
  }, [pushLocalActivity]);

  const exportData = useCallback(() => state, [state]);

  const metrics = useMemo(() => {
    const fundsRaised = state.funders.reduce((sum, f) => sum + (Number(f.committed) || 0), 0);
    const activeSubs = state.subscribers.filter((s) => s.status === 'active').length;
    const signedMous = state.partners.filter((p) => p.status === 'signed').length;
    const inDiscussion = state.partners.filter((p) => p.status === 'in_discussion').length;
    const openGrants = state.grants.filter((g) => !['awarded', 'rejected'].includes(g.status));
    const grantPipeline = openGrants.reduce((sum, g) => sum + (Number(g.amount) || 0), 0);
    const activeVolunteers = state.volunteers.filter((v) => v.status === 'active').length;
    const volunteerHrs = state.volunteers.reduce((sum, v) => sum + (Number(v.hours) || 0), 0);
    const overdueFollowups = state.followups.filter((f) => f.status === 'overdue').length;
    return {
      fundsRaised, activeSubs, signedMous, inDiscussion, grantPipeline,
      openGrantCount: openGrants.length, activeVolunteers, volunteerHrs, overdueFollowups,
      counts: {
        funders: state.funders.length, grants: state.grants.length, partners: state.partners.length,
        subscribers: state.subscribers.length, volunteers: state.volunteers.length, followups: state.followups.length,
      },
    };
  }, [state]);

  const value = {
    state, lang, setLang, metrics, loading, error,
    updateSettings, upsertItem, deleteItem, clearActivityLog,
    importData, exportData, logActivity,
  };

  return <LdbContext.Provider value={value}>{children}</LdbContext.Provider>;
}

export function useLdb() {
  const ctx = useContext(LdbContext);
  if (!ctx) throw new Error('useLdb must be used within LdbProvider');
  return ctx;
}
